/**
 * Hub client: one WebSocket to the UserHub DO speaking `@viko/protocol` v0 frames.
 *
 *  - `hello{role:'client'}` → `welcome` (server time corrects the signing clock).
 *  - Per-run subscriptions keep their own cursor (the last seq delivered to the view).
 *    `subscribe{runId, fromSeq: cursor}` is the same frame for first open, reconnect and
 *    coming back online, so resume is exact: the hub replays `seq > cursor`, then goes live.
 *  - Dedupe: anything at or below a run's cursor is dropped (replays overlap live fan-out
 *    around a reconnect). Seq gaps are legal — live-only `asst.delta`s are not stored, so a
 *    replay skips their seqs.
 *  - Commands: `command{SignedCommand}` → `command.result{commandId}`. While disconnected,
 *    commands wait in an outbox and are re-sent with the *same* envelope on reconnect; the
 *    envelope id makes that idempotent (`duplicate` returns the original outcome).
 *  - Reconnect: full-jitter exponential backoff, reset after a stable `welcome` (a socket that
 *    drops within seconds keeps backing off) and never sooner than a hub `rate_limited` answer's
 *    `retryAfterMs`; keepalive pings (`{"type":"ping"}`, auto-answered by the hub without waking
 *    the DO) detect half-open sockets; `online`/`offline` browser events short-circuit the wait.
 *  - Control-plane frames `inbox`, `inbox.ranked`, `run` and `work` feed the Inbox, Now and the
 *    work-model queries.
 */
import type { SignedCommand } from '@viko/crypto'
import {
  backoffDelay,
  ContentEventBatchFrame,
  ENCRYPTED_EVENT_BATCH_BYTES,
  ENCRYPTED_EVENTS_CAPABILITY,
  PROTOCOL_VERSION,
  RunEventsPageFrame, // materialized-state
  RunSnapshotBatchFrame, // materialized-state
  rateLimitRetryAfter,
  type TranscriptEvent,
} from '@viko/protocol'
import { normalizeRun } from './api.js'
import { clock } from './clock.js'
import type { ContentVault } from './content-vault.js'
import type { CommandOutcome, InboxItem, RankedInbox, RunSummary, WorkFrame } from './types.js'

export type ConnState = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'offline'

export interface SocketLike {
  readonly readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((ev: unknown) => void) | null
  onmessage: ((ev: { data: unknown }) => void) | null
  onclose: ((ev: { code?: number; reason?: string }) => void) | null
  onerror: ((ev: unknown) => void) | null
}

export type SocketFactory = () => SocketLike

export interface BatchMeta {
  replay: boolean
  /** True for the first batch after a reconnect resume (the view shows a marker). */
  resumed: boolean
  /** A replay page with more history to follow (encrypted paging); absent = last page. */
  more?: boolean
}

export interface RunListener {
  onEvents(events: TranscriptEvent[], meta: BatchMeta): void
  /** Connection transitions while subscribed (drives the viewer's stale state + markers). */
  onConnection?(state: ConnState, info: { cursor: number; attempt: number }): void
}

interface Sub {
  runId: string
  cursor: number
  listeners: Set<RunListener>
  /** Set when a reconnect resubscribed; cleared by the first batch after it. */
  resumePending: boolean
  /** not_found retries (run not yet registered at the hub). */
  retries: number
  /** Highest authenticated epoch delivered for this run; later events may not go below it. */
  epoch?: number
}

interface PendingCommand {
  command: SignedCommand
  resolve: (r: CommandOutcome) => void
  reject: (e: Error) => void
  sent: boolean
  timer?: ReturnType<typeof setTimeout>
}

export interface HubOptions {
  encryptedEvents?: {
    accountId: string | (() => Promise<string>)
    vault: Pick<ContentVault, 'transcriptBatch'>
    minimumEpoch?: number
    onError?: () => void
  }

  socket: SocketFactory
  deviceId: () => string | undefined
  softwareVersion?: string
  backoff?: { baseMs: number; maxMs: number }
  heartbeatMs?: number
  /** A command with no result after this long is rejected (the caller may retry the same id). */
  commandTimeoutMs?: number
  random?: () => number
  timers?: {
    setTimeout: typeof setTimeout
    clearTimeout: typeof clearTimeout
    setInterval: typeof setInterval
    clearInterval: typeof clearInterval
  }
}

const OPEN = 1
/** After waking from sleep: how long a ping may go unanswered before the socket is dropped. */
const WAKE_PROBE_MS = 3_000
/**
 * Keepalive cadence. The ping is the exact string `{"type":"ping"}`, which the hub answers with
 * `{"type":"pong"}` by WebSocket auto-response: it never wakes the hibernating Durable Object.
 * Hidden tabs are throttled by the browser to ≤ 1 timer/min anyway; 30 s keeps proxies open.
 */
export const HEARTBEAT_MS = 30_000
export const KEEPALIVE_PING = '{"type":"ping"}'
/** A connection that drops sooner than this after `welcome` keeps growing the backoff. */
const STABLE_MS = 10_000

/** live-preview-tunnel: a relayed preview frame (`f` is base64url ciphertext). */
export type PreviewHubFrame =
  | { type: 'preview.frame'; previewId: string; f: string }
  | { type: 'preview.end'; previewId: string; reason: string }

export class HubClient {
  private ws: SocketLike | null = null
  private state: ConnState = 'idle'
  private attempt = 0
  private subs = new Map<string, Sub>()
  private pending = new Map<string, PendingCommand>()
  private stateListeners = new Set<(s: ConnState) => void>()
  private inboxListeners = new Set<(items: InboxItem[]) => void>()
  private runListeners = new Set<(run: RunSummary) => void>()
  private rankedListeners = new Set<(r: RankedInbox) => void>()
  private workListeners = new Set<(f: WorkFrame) => void>()
  private retryTimer: ReturnType<typeof setTimeout> | undefined
  private heartbeat: ReturnType<typeof setInterval> | undefined
  private probeTimer: ReturnType<typeof setTimeout> | undefined
  private lastMessageAt = 0
  private encryptedQueuedBytes = 0
  private encryptedChain: Promise<void> = Promise.resolve()
  private wanted = false
  private hadWelcome = false
  private liveAt = 0
  private welcomeAttempt = 0
  /** Hub said rate_limited: no reconnect before this time (ms epoch). */
  private notBefore = 0
  private pingSentAt = 0
  private rtt: number | undefined
  private readonly t: NonNullable<HubOptions['timers']>

  constructor(private readonly opts: HubOptions) {
    this.t = opts.timers ?? {
      setTimeout: globalThis.setTimeout.bind(globalThis),
      clearTimeout: globalThis.clearTimeout.bind(globalThis),
      setInterval: globalThis.setInterval.bind(globalThis),
      clearInterval: globalThis.clearInterval.bind(globalThis),
    }
  }

  // ---- connection ---------------------------------------------------------------------------

  get connection(): ConnState {
    return this.state
  }

  /** Last keepalive round trip, measured locally (the auto-response carries no timestamp). */
  get rttMs(): number | undefined {
    return this.rtt
  }

  connect() {
    this.wanted = true
    if (this.ws) return
    this.open()
  }

  disconnect() {
    this.wanted = false
    if (this.retryTimer) this.t.clearTimeout(this.retryTimer)
    this.stopHeartbeat()
    const ws = this.ws
    this.ws = null
    if (ws) {
      ws.onclose = null
      ws.close(1000, 'bye')
    }
    this.setState('idle')
  }

  /** Account lifecycle boundary, unlike temporary network disconnects. */
  resetAccount() {
    this.disconnect()
    for (const p of this.pending.values()) {
      if (p.timer) this.t.clearTimeout(p.timer)
      p.reject(new Error('Account changed'))
    }
    this.pending.clear()
    this.subs.clear()
    this.hadWelcome = false
  }

  /** Browser went offline: stop retrying until `online`. */
  goOffline() {
    this.dropSocket()
    this.setState('offline')
  }

  /** Browser came back online (or the app returned to the foreground): reconnect now. */
  wake() {
    if (!this.wanted) return
    if (this.state === 'live' && this.ws) {
      // ---- m13-reliability: sleep/wake ----
      // Timers were suspended while the page slept and the socket may be half-open (no close
      // event ever comes). Silent past the dead threshold: drop now and resume from the cursor.
      // Silent for more than a beat: ping, and drop if nothing answers the probe.
      const every = this.opts.heartbeatMs ?? HEARTBEAT_MS
      const silent = Date.now() - this.lastMessageAt
      if (silent > every * 2.5) this.simulateDrop()
      else if (silent > every) this.probe()
      // ---- end m13-reliability ----
      return
    }
    // Rate limited: the scheduled retry (≥ retryAfter) stands; waking never jumps the queue.
    if (this.notBefore > Date.now() && this.retryTimer) return
    if (this.retryTimer) this.t.clearTimeout(this.retryTimer)
    this.retryTimer = undefined
    if (!this.ws) this.open()
  }

  /** Test/diagnostic hook: sever the socket as if the network dropped. */
  simulateDrop() {
    const ws = this.ws
    if (!ws) return
    const handler = ws.onclose
    ws.onclose = null
    try {
      ws.close(4001, 'dropped')
    } catch {
      // already closed
    }
    handler?.({ code: 1006, reason: 'simulated' })
  }

  onState(cb: (s: ConnState) => void) {
    this.stateListeners.add(cb)
    return () => this.stateListeners.delete(cb)
  }
  onInbox(cb: (items: InboxItem[]) => void) {
    this.inboxListeners.add(cb)
    return () => this.inboxListeners.delete(cb)
  }
  onRun(cb: (run: RunSummary) => void) {
    this.runListeners.add(cb)
    return () => this.runListeners.delete(cb)
  }
  /** `inbox.ranked{sessions, digest, snoozed}` (sent next to every `inbox` frame). */
  onRanked(cb: (r: RankedInbox) => void) {
    this.rankedListeners.add(cb)
    return () => this.rankedListeners.delete(cb)
  }
  /** `work{entity, op, id, data?}`: a work-model record changed. */
  onWork(cb: (f: WorkFrame) => void) {
    this.workListeners.add(cb)
    return () => this.workListeners.delete(cb)
  }

  private setState(s: ConnState) {
    if (s === this.state) return
    this.state = s
    for (const cb of this.stateListeners) cb(s)
    for (const sub of this.subs.values())
      for (const l of sub.listeners)
        l.onConnection?.(s, { cursor: sub.cursor, attempt: this.attempt })
  }

  private open() {
    this.setState(this.hadWelcome ? 'reconnecting' : 'connecting')
    let ws: SocketLike
    try {
      ws = this.opts.socket()
    } catch {
      this.scheduleRetry()
      return
    }
    this.ws = ws
    ws.onopen = () => {
      this.lastMessageAt = Date.now()
      this.sendRaw({
        type: 'hello',
        v: PROTOCOL_VERSION,
        role: 'client',
        ...(this.opts.encryptedEvents ? { capabilities: [ENCRYPTED_EVENTS_CAPABILITY] } : {}),
        peerId: this.opts.deviceId() ?? 'anonymous',
        softwareVersion: this.opts.softwareVersion ?? 'web',
      })
    }
    ws.onmessage = (ev) => {
      if (this.ws === ws) this.onMessage(ev.data)
    }
    ws.onerror = () => {}
    ws.onclose = () => {
      if (this.ws !== ws) return
      this.ws = null
      this.stopHeartbeat()
      // A socket that flaps right after welcome (e.g. refused by a rate limit) never resets the
      // backoff: reconnects keep spreading out instead of looping.
      if (this.liveAt && Date.now() - this.liveAt < STABLE_MS) this.attempt = this.welcomeAttempt
      this.liveAt = 0
      for (const p of this.pending.values()) p.sent = false
      if (!this.wanted) return this.setState('idle')
      if (this.state === 'offline') return
      this.scheduleRetry()
    }
  }

  private dropSocket() {
    const ws = this.ws
    this.ws = null
    this.stopHeartbeat()
    for (const p of this.pending.values()) p.sent = false
    if (ws) {
      ws.onclose = null
      try {
        ws.close(4000, 'offline')
      } catch {
        // already closed
      }
    }
  }

  /** Full-jitter exponential backoff: uniform(0, min(max, base·2^attempt)). */
  nextDelay(attempt = this.attempt): number {
    const { baseMs, maxMs } = this.opts.backoff ?? { baseMs: 500, maxMs: 15_000 }
    const cap = Math.min(maxMs, baseMs * 2 ** attempt)
    return Math.max(100, Math.round((this.opts.random ?? Math.random)() * cap))
  }

  private scheduleRetry() {
    this.setState('reconnecting')
    const delay = Math.max(this.nextDelay(), this.notBefore - Date.now())
    this.attempt++
    if (this.retryTimer) this.t.clearTimeout(this.retryTimer)
    this.retryTimer = this.t.setTimeout(() => {
      this.retryTimer = undefined
      if (this.wanted && !this.ws) this.open()
    }, delay)
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    const every = this.opts.heartbeatMs ?? HEARTBEAT_MS
    this.heartbeat = this.t.setInterval(() => {
      if (!this.ws) return
      if (Date.now() - this.lastMessageAt > every * 2.5) {
        // Half-open socket: no pong for >2 beats.
        this.simulateDrop()
        return
      }
      this.ping()
    }, every)
  }

  private stopHeartbeat() {
    if (this.heartbeat) this.t.clearInterval(this.heartbeat)
    this.heartbeat = undefined
    if (this.probeTimer) this.t.clearTimeout(this.probeTimer)
    this.probeTimer = undefined
  }

  /** One ping; the socket is dropped if nothing at all arrives within WAKE_PROBE_MS. */
  private probe() {
    const ws = this.ws
    const sentAt = Date.now()
    if (!ws || this.probeTimer) return
    this.ping()
    this.probeTimer = this.t.setTimeout(() => {
      this.probeTimer = undefined
      if (this.ws === ws && this.lastMessageAt < sentAt) this.simulateDrop()
    }, WAKE_PROBE_MS)
  }

  /** The exact auto-response request string (no timestamp: the hub answers without waking). */
  private ping() {
    const ws = this.ws
    if (!ws || ws.readyState !== OPEN) return
    try {
      ws.send(KEEPALIVE_PING)
      if (!this.pingSentAt) this.pingSentAt = Date.now()
    } catch {
      // closed meanwhile; onclose handles it
    }
  }

  private sendRaw(frame: unknown): boolean {
    const ws = this.ws
    if (!ws || ws.readyState !== OPEN) return false
    try {
      ws.send(JSON.stringify(frame))
      return true
    } catch {
      return false
    }
  }

  // ---- inbound ------------------------------------------------------------------------------

  private onMessage(data: unknown) {
    this.lastMessageAt = Date.now()
    let msg: { type?: string; [k: string]: unknown }
    try {
      msg = typeof data === 'string' ? JSON.parse(data) : (data as typeof msg)
    } catch {
      return
    }
    switch (msg.type) {
      case 'welcome': {
        if (this.opts.encryptedEvents && msg.encryptedEvents !== 1) {
          this.failEncrypted()
          return
        }
        if (typeof msg.serverTime === 'number') clock.setServerTime(msg.serverTime)
        const resumed = this.hadWelcome
        this.hadWelcome = true
        this.welcomeAttempt = this.attempt
        this.attempt = 0
        this.liveAt = Date.now()
        this.pingSentAt = 0
        this.setState('live')
        this.startHeartbeat()
        for (const sub of this.subs.values()) {
          sub.resumePending = resumed
          this.sendRaw({
            type: this.opts.encryptedEvents ? 'content.subscribe' : 'subscribe',
            runId: sub.runId,
            fromSeq: sub.cursor,
          })
        }
        for (const p of this.pending.values()) this.flushCommand(p)
        return
      }
      case 'content.event.batch': {
        const content = this.opts.encryptedEvents
        const socket = this.ws
        if (!content) return
        if (this.state !== 'live') {
          this.failEncrypted()
          return
        }
        const bytes = new TextEncoder().encode(JSON.stringify(msg)).length
        if (
          bytes > ENCRYPTED_EVENT_BATCH_BYTES ||
          this.encryptedQueuedBytes + bytes > 8 * ENCRYPTED_EVENT_BATCH_BYTES
        ) {
          this.failEncrypted()
          return
        }
        this.encryptedQueuedBytes += bytes
        this.encryptedChain = this.encryptedChain
          .then(async () => {
            if (socket !== this.ws) return
            const frame = ContentEventBatchFrame.parse(msg)
            const sub = this.subs.get(frame.runId)
            if (!sub) return
            const events = await content.vault.transcriptBatch(
              typeof content.accountId === 'string' ? content.accountId : await content.accountId(),
              frame.runId,
              frame.events,
              Math.max(content.minimumEpoch ?? 1, sub.epoch ?? 1),
            )
            if (socket !== this.ws || this.subs.get(frame.runId) !== sub) return
            // j-privacy: epochs never decrease within a run (retired-key forgeries fail).
            sub.epoch = Math.max(sub.epoch ?? 1, ...frame.events.map((e) => e.context.epoch))
            const fresh = events.filter((e) => e.seq > sub.cursor)
            if (fresh.length && fresh[0]!.seq !== sub.cursor + 1)
              throw new Error('transcript sequence gap')
            this.onBatch(frame.runId, events, frame.replay === true, frame.more === true)
            if (frame.more)
              this.sendRaw({ type: 'content.subscribe', runId: frame.runId, fromSeq: sub.cursor })
          })
          .catch(() => {
            if (socket === this.ws) this.failEncrypted()
          })
          .finally(() => {
            this.encryptedQueuedBytes -= bytes
          })
        return
      }
      case 'event.batch':
        if (this.opts.encryptedEvents) {
          this.failEncrypted()
          return
        }
        return this.onBatch(
          msg.runId as string,
          (msg.events as TranscriptEvent[]) ?? [],
          msg.replay === true,
        )
      case 'command.result': {
        const id = msg.commandId as string
        const p = this.pending.get(id)
        if (!p) return
        const status = msg.status as CommandOutcome['status']
        // Encrypted callers need the authenticated daemon outcome, not relay acceptance.
        if (
          this.opts.encryptedEvents &&
          (status === 'accepted' ||
            status === 'delivered' ||
            (status === 'duplicate' &&
              ['accepted', 'delivered'].includes(
                String((msg.result as { original?: unknown } | undefined)?.original),
              )))
        )
          return
        // Legacy callers retain their accepted/queued semantics.
        this.pending.delete(id)
        if (p.timer) this.t.clearTimeout(p.timer)
        p.resolve({
          commandId: id,
          status,
          ...(typeof msg.reason === 'string' ? { reason: msg.reason } : {}),
          ...(typeof msg.error === 'string' ? { error: msg.error } : {}),
          ...(msg.result !== undefined ? { result: msg.result } : {}),
        })
        return
      }
      case 'pong':
        // Auto-response `{"type":"pong"}` has no `t`: the round trip is measured locally.
        if (this.pingSentAt) this.rtt = Date.now() - this.pingSentAt
        this.pingSentAt = 0
        return
      case 'inbox':
        for (const cb of this.inboxListeners) cb((msg.items as InboxItem[]) ?? [])
        return
      case 'run':
        for (const cb of this.runListeners) cb(normalizeRun(msg.run as Record<string, unknown>))
        return
      case 'inbox.ranked': {
        const r: RankedInbox = {
          sessions: (msg.sessions as RankedInbox['sessions']) ?? [],
          digest: (msg.digest as RankedInbox['digest']) ?? [],
          snoozed: (msg.snoozed as RankedInbox['snoozed']) ?? [],
        }
        for (const cb of this.rankedListeners) cb(r)
        return
      }
      case 'work':
        if (typeof msg.entity === 'string' && typeof msg.id === 'string')
          for (const cb of this.workListeners) cb(msg as unknown as WorkFrame)
        return
      case 'error': {
        const retryAfter = rateLimitRetryAfter(msg)
        if (retryAfter !== undefined) {
          this.notBefore = Math.max(this.notBefore, Date.now() + retryAfter)
          return
        }
        if (msg.code === 'not_found' && typeof msg.runId === 'string') {
          // A just-started run may not be known to the hub yet: retry the subscription.
          const runId = msg.runId
          const sub = this.subs.get(runId)
          if (sub && sub.retries < 20) {
            sub.retries++
            this.t.setTimeout(
              () => {
                const s = this.subs.get(runId)
                if (s)
                  this.sendRaw({
                    type: this.opts.encryptedEvents ? 'content.subscribe' : 'subscribe',
                    runId,
                    fromSeq: s.cursor,
                  })
              },
              backoffDelay(sub.retries - 1, {
                baseMs: 500,
                maxMs: 5_000,
                random: this.opts.random ?? Math.random,
              }),
            )
          }
          return
        }
        if (msg.code === 'unsupported_version') {
          this.wanted = false
          this.dropSocket()
          this.setState('offline')
        }
        return
      }
      // ---- materialized-state: run snapshot reads, pushes and older event pages ----
      case 'content.snapshot.batch':
      case 'content.events.page': {
        const id = String(msg.reqId ?? '')
        const p = this.requests.get(id)
        if (!p) return
        this.requests.delete(id)
        this.t.clearTimeout(p.timer)
        p.resolve(msg)
        return
      }
      case 'content.snapshot':
        for (const cb of this.snapshotListeners) cb(msg.snapshot)
        return
      // ---- end materialized-state ----
      // ---- live-preview-tunnel: opaque E2E preview frames from the serving machine ----
      case 'preview.frame':
      case 'preview.end':
        for (const cb of this.previewListeners) cb(msg as PreviewHubFrame)
        return
      // ---- end live-preview-tunnel ----
      default:
        return
    }
  }

  // ---- live-preview-tunnel: relay for live previews (ciphertext only) ----
  private readonly previewListeners = new Set<(f: PreviewHubFrame) => void>()
  onPreviewFrame(cb: (f: PreviewHubFrame) => void): () => void {
    this.previewListeners.add(cb)
    return () => this.previewListeners.delete(cb)
  }
  sendPreviewFrame(frame: {
    type: 'preview.frame'
    machineId: string
    previewId: string
    f: string
  }): boolean {
    return this.sendRaw(frame)
  }
  // ---- end live-preview-tunnel ----

  // ---- materialized-state: run snapshot reads, pushes and older event pages ----
  private requests = new Map<
    string,
    {
      resolve: (m: unknown) => void
      reject: (e: Error) => void
      timer: ReturnType<typeof setTimeout>
    }
  >()
  private requestSeq = 0
  private snapshotListeners = new Set<(raw: unknown) => void>()

  /** Sealed `run.snapshot` pushes (a daemon published a newer materialized state). */
  onSnapshot(cb: (raw: unknown) => void) {
    this.snapshotListeners.add(cb)
    return () => this.snapshotListeners.delete(cb)
  }

  private whenLive(ms: number): Promise<void> {
    if (this.state === 'live') return Promise.resolve()
    return new Promise((resolve, reject) => {
      const timer = this.t.setTimeout(() => {
        off()
        reject(new Error('Hub offline'))
      }, ms)
      const off = this.onState((s) => {
        if (s !== 'live') return
        this.t.clearTimeout(timer)
        off()
        resolve()
      })
    })
  }

  private async request(frame: Record<string, unknown>, timeoutMs = 15_000): Promise<unknown> {
    await this.whenLive(timeoutMs)
    const reqId = `mq${++this.requestSeq}`
    return new Promise((resolve, reject) => {
      const timer = this.t.setTimeout(() => {
        this.requests.delete(reqId)
        reject(new Error('Hub request timed out'))
      }, timeoutMs)
      this.requests.set(reqId, { resolve, reject, timer })
      if (!this.sendRaw({ ...frame, reqId })) {
        this.requests.delete(reqId)
        this.t.clearTimeout(timer)
        reject(new Error('Hub offline'))
      }
    })
  }

  /** Newest sealed snapshot per run (ciphertext; the caller authenticates) + the hub's heads. */
  async runSnapshots(runIds: readonly string[]) {
    const f = RunSnapshotBatchFrame.parse(
      await this.request({ type: 'content.snapshot.get', runIds: [...runIds] }),
    )
    return { snapshots: f.snapshots as unknown[], heads: f.heads }
  }

  /** One authenticated page of a run's events after `after` (older turns of a window). */
  async eventPage(
    runId: string,
    after: number,
  ): Promise<{ events: TranscriptEvent[]; more: boolean }> {
    const content = this.opts.encryptedEvents
    if (!content) throw new Error('Encrypted events unavailable')
    const f = RunEventsPageFrame.parse(
      await this.request({ type: 'content.events.read', runId, after }),
    )
    if (f.runId !== runId) throw new Error('Wrong event page')
    f.events.forEach((e, i) => {
      if (e.context.seq !== after + i + 1) throw new Error('transcript sequence gap')
    })
    if (!f.events.length) return { events: [], more: false }
    const accountId =
      typeof content.accountId === 'string' ? content.accountId : await content.accountId()
    const events = await content.vault.transcriptBatch(
      accountId,
      runId,
      f.events,
      content.minimumEpoch ?? 1,
    )
    return { events, more: f.more }
  }
  // ---- end materialized-state ----

  private failEncrypted() {
    this.wanted = false
    this.dropSocket()
    this.setState('offline')
    this.opts.encryptedEvents?.onError?.()
  }

  private onBatch(runId: string, events: TranscriptEvent[], replay: boolean, more = false) {
    const sub = this.subs.get(runId)
    if (!sub) return
    const fresh = dedupeAfter(events, sub.cursor)
    if (!fresh.length) return
    sub.cursor = fresh[fresh.length - 1]?.seq ?? sub.cursor
    const resumed = sub.resumePending
    sub.resumePending = false
    for (const l of sub.listeners)
      l.onEvents(fresh, { replay, resumed, ...(replay && more ? { more: true } : {}) })
  }

  // ---- subscriptions ------------------------------------------------------------------------

  /**
   * Stream `runId` into `listener`, starting after `fromSeq` (0 = full history). Several
   * listeners share one subscription; the cursor only moves forward.
   */
  subscribe(runId: string, fromSeq: number, listener: RunListener): () => void {
    let sub = this.subs.get(runId)
    if (!sub) {
      sub = { runId, cursor: fromSeq, listeners: new Set(), resumePending: false, retries: 0 }
      this.subs.set(runId, sub)
      if (!this.opts.encryptedEvents || this.state === 'live')
        this.sendRaw({
          type: this.opts.encryptedEvents ? 'content.subscribe' : 'subscribe',
          runId,
          fromSeq,
        })
    }
    sub.listeners.add(listener)
    this.connect()
    return () => {
      const s = this.subs.get(runId)
      if (!s) return
      s.listeners.delete(listener)
      if (s.listeners.size === 0) {
        this.subs.delete(runId)
        this.sendRaw({ type: 'unsubscribe', runId })
      }
    }
  }

  cursorOf(runId: string): number | undefined {
    return this.subs.get(runId)?.cursor
  }

  /** Persist a read position (unread counts, cross-device resume). */
  markRead(runId: string, seq: number) {
    this.sendRaw({ type: 'cursor', runId, seq })
  }

  // ---- commands -----------------------------------------------------------------------------

  /**
   * Send a signed command and resolve with its `command.result`. Re-sending the same
   * SignedCommand (same envelope id) is safe; while offline it waits in the outbox until the
   * envelope expires.
   */
  send(command: SignedCommand): Promise<CommandOutcome> {
    const id = command.envelope.id
    const existing = this.pending.get(id)
    if (existing)
      return new Promise((resolve, reject) => {
        const prev = { resolve: existing.resolve, reject: existing.reject }
        existing.resolve = (r) => {
          prev.resolve(r)
          resolve(r)
        }
        existing.reject = (e) => {
          prev.reject(e)
          reject(e)
        }
      })
    this.connect()
    return new Promise((resolve, reject) => {
      const p: PendingCommand = { command, resolve, reject, sent: false }
      this.pending.set(id, p)
      const ttl = Math.max(0, command.envelope.expiresAt - clock.now())
      p.timer = this.t.setTimeout(
        () => {
          if (!this.pending.has(id)) return
          this.pending.delete(id)
          reject(new CommandTimeoutError(id, p.sent))
        },
        Math.min(ttl, this.opts.commandTimeoutMs ?? 30_000),
      )
      if (this.state === 'live') this.flushCommand(p)
    })
  }

  pendingCommands(): number {
    return this.pending.size
  }

  private flushCommand(p: PendingCommand) {
    if (p.command.envelope.expiresAt <= clock.now()) {
      this.pending.delete(p.command.envelope.id)
      if (p.timer) this.t.clearTimeout(p.timer)
      p.reject(new CommandTimeoutError(p.command.envelope.id, p.sent))
      return
    }
    p.sent = this.sendRaw({ type: 'command', command: p.command }) || p.sent
  }
}

export class CommandTimeoutError extends Error {
  constructor(
    readonly commandId: string,
    readonly wasSent: boolean,
  ) {
    super(wasSent ? 'No reply from the hub yet.' : 'Could not reach the hub.')
  }
}

/** Events strictly after `cursor`, ascending and unique by seq. */
export function dedupeAfter(events: readonly TranscriptEvent[], cursor: number): TranscriptEvent[] {
  const out: TranscriptEvent[] = []
  let last = cursor
  const sorted = isAscending(events) ? events : [...events].sort((a, b) => a.seq - b.seq)
  for (const e of sorted) {
    if (e.seq <= last) continue
    out.push(e)
    last = e.seq
  }
  return out
}

function isAscending(events: readonly TranscriptEvent[]) {
  for (let i = 1; i < events.length; i++) {
    const a = events[i - 1]
    const b = events[i]
    if (a && b && b.seq < a.seq) return false
  }
  return true
}

/** Browser WebSocket factory for `/ws/client` (session cookie authenticates; Origin checked). */
export function browserSocket(origin: string): SocketFactory {
  return () => {
    const base = origin || location.origin
    const url = new URL('/ws/client', base)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    return new WebSocket(url) as unknown as SocketLike
  }
}
